import type { SupportedLanguage } from '../../i18n';

/**
 * Locale mapping for Intl APIs
 * Maps our language codes to full locale codes
 */
const LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: 'en-US',
  it: 'it-IT',
  es: 'es-ES',
  fr: 'fr-FR',
};

/**
 * Get full locale code from language
 */
export function getLocale(language: SupportedLanguage): string {
  return LOCALE_MAP[language] || 'en-US';
}

/**
 * Date formatting options
 */
export interface DateFormatOptions {
  format?: 'short' | 'medium' | 'long' | 'full';
  includeTime?: boolean;
  timeFormat?: 'short' | 'medium';
}

/**
 * Format a date according to the current locale
 */
export function formatDate(
  date: Date | string | number,
  language: SupportedLanguage,
  options: DateFormatOptions = {}
): string {
  const { format = 'medium', includeTime = false, timeFormat = 'short' } = options;

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const locale = getLocale(language);

  const dateStyleMap: Record<string, Intl.DateTimeFormatOptions['dateStyle']> = {
    short: 'short',
    medium: 'medium',
    long: 'long',
    full: 'full',
  };

  const timeStyleMap: Record<string, Intl.DateTimeFormatOptions['timeStyle']> = {
    short: 'short',
    medium: 'medium',
  };

  const formatOptions: Intl.DateTimeFormatOptions = {
    dateStyle: dateStyleMap[format],
  };

  if (includeTime) {
    formatOptions.timeStyle = timeStyleMap[timeFormat];
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
}

/**
 * Format a number according to the current locale
 */
export function formatNumber(
  num: number,
  language: SupportedLanguage,
  options: Intl.NumberFormatOptions = {}
): string {
  const locale = getLocale(language);
  return new Intl.NumberFormat(locale, options).format(num);
}

/**
 * Currency formatting options
 */
export interface CurrencyFormatOptions {
  currency?: string;
  display?: 'symbol' | 'code' | 'name';
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format a currency amount according to the current locale
 */
export function formatCurrency(
  amount: number,
  language: SupportedLanguage,
  options: CurrencyFormatOptions = {}
): string {
  const {
    currency = 'EUR',
    display = 'symbol',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  const locale = getLocale(language);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: display,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

/**
 * Format relative time (e.g., "5 minutes ago", "in 2 days")
 */
export function formatRelativeTime(
  date: Date | string | number,
  language: SupportedLanguage
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const locale = getLocale(language);
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);
  const diffWeeks = Math.round(diffDays / 7);
  const diffMonths = Math.round(diffDays / 30);
  const diffYears = Math.round(diffDays / 365);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSeconds) < 60) {
    return rtf.format(diffSeconds, 'second');
  } else if (Math.abs(diffMinutes) < 60) {
    return rtf.format(diffMinutes, 'minute');
  } else if (Math.abs(diffHours) < 24) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffDays) < 7) {
    return rtf.format(diffDays, 'day');
  } else if (Math.abs(diffWeeks) < 4) {
    return rtf.format(diffWeeks, 'week');
  } else if (Math.abs(diffMonths) < 12) {
    return rtf.format(diffMonths, 'month');
  } else {
    return rtf.format(diffYears, 'year');
  }
}

/**
 * Format a list of items according to the locale
 * e.g., "Alice, Bob, and Charlie" in English vs "Alice, Bob e Charlie" in Italian
 */
export function formatList(
  items: string[],
  language: SupportedLanguage,
  type: 'conjunction' | 'disjunction' = 'conjunction'
): string {
  const locale = getLocale(language);

  return new Intl.ListFormat(locale, {
    style: 'long',
    type,
  }).format(items);
}

/**
 * Format a date range
 */
export function formatDateRange(
  startDate: Date | string | number,
  endDate: Date | string | number,
  language: SupportedLanguage
): string {
  const locale = getLocale(language);

  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
  });

  // Use formatRange if available, otherwise fallback to manual formatting
  if (typeof formatter.formatRange === 'function') {
    return formatter.formatRange(start, end);
  }

  // Fallback for browsers without formatRange support
  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

/**
 * Format height in appropriate unit for locale
 * @param heightCm Height in centimeters
 */
export function formatHeight(
  heightCm: number,
  language: SupportedLanguage
): string {
  // For now, always use metric (cm) - could add feet/inches for en-US later
  return `${formatNumber(heightCm, language)} cm`;
}

/**
 * Format weight in appropriate unit for locale
 * @param weightKg Weight in kilograms
 */
export function formatWeight(
  weightKg: number,
  language: SupportedLanguage
): string {
  // For now, always use metric (kg) - could add pounds for en-US later
  return `${formatNumber(weightKg, language)} kg`;
}
